/**
 * PaxPayment Capacitor plugin'inin tipli JS sarmalayıcısı.
 *
 * Native tarafı `web/native/pax-payment/PaxPaymentPlugin.kt` — A920 üstündeki
 * banka ödeme uygulamasını Android Intent ile tetikler.
 *
 * `@capacitor/core` LAZY (dinamik) import edilir — yalnızca native Capacitor
 * ortamında ödeme tetiklendiğinde yüklenir. Bu sayede web/PWA build'i ve dev
 * sunucusu `@capacitor/core` paketi kurulu olmasa da çalışır (paket
 * `package.json`'da tanımlı; native build'de `pnpm install` ile gelir).
 *
 * Kullanmadan önce `isPaxPaymentAvailable()` ile kontrol edilmelidir.
 */

import type { IntentPayload } from "./paymentTerminal";

/** Native plugin'in döndürdüğü ham sonuç (PaxPaymentPlugin.kt ile birebir). */
export interface PaxIntentResult {
  /** "000000" = onay; "CANCELLED" = iptal; "ERROR" / diğer = ret. */
  resultCode: string;
  approvalCode?: string;
  transactionNumber?: string;
  /** Maskeli kart numarası. */
  cardNumber?: string;
  cardType?: string;
  message?: string;
}

/** Native PaxPayment plugin arabirimi. */
export interface PaxPaymentPlugin {
  /**
   * Banka ödeme uygulamasını Intent ile başlatır.
   * `payload` backend'in `POST /terminal-transactions/` response'undaki
   * `intent_payload` alanıdır.
   */
  startIntent(payload: IntentPayload): Promise<PaxIntentResult>;
}

/** Capacitor + native PaxPayment plugin'i mevcut mu? */
export function isPaxPaymentAvailable(): boolean {
  const w = window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean; Plugins?: Record<string, unknown> };
  };
  return Boolean(
    w.Capacitor?.isNativePlatform?.() && w.Capacitor.Plugins?.PaxPayment,
  );
}

/** `@capacitor/core` modül adı — değişken üzerinden verilir ki Vite bunu
 *  statik olarak analiz edip web build'inde çözmeye çalışmasın. Paket yalnızca
 *  native Capacitor ortamında (APK) gerçekten kuruludur. */
const CAPACITOR_CORE = "@capacitor/core";

/**
 * PaxPayment plugin'ini Intent ile çalıştırır.
 *
 * `@capacitor/core` yalnızca burada, çağrı anında ve değişken üzerinden
 * dinamik import edilir — web/PWA build'i bu paketi hiç çözmeye çalışmaz
 * (paket kurulu olmasa da dev sunucu/build çalışır). Bu fonksiyon zaten
 * yalnızca native ortamda çağrılır (önce `isPaxPaymentAvailable()`).
 */
export async function startPaxIntent(
  payload: IntentPayload,
): Promise<PaxIntentResult> {
  const mod = (await import(/* @vite-ignore */ CAPACITOR_CORE)) as {
    registerPlugin: <T>(name: string) => T;
  };
  const plugin = mod.registerPlugin<PaxPaymentPlugin>("PaxPayment");
  return plugin.startIntent(payload);
}
